import React from 'react'

import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { LineChartRenderer } from 'features/charts-v2/strategies/LineChartComponents'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

export class LineChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.LINE
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []
    const isDualMetric = props.selectedMetrics.length > 1

    // Note: Time is now always present for LINE charts (not in breakdowns array)

    // Breakdown + dual metrics: use dual-axis multi-line transformation
    if (breakdowns.length >= 1 && isDualMetric) {
      return dataTransformationService.transformForDualAxisMultiLineChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
      )
    }

    // No breakdown + dual metrics: use dual-axis transformation
    if (isDualMetric) {
      return dataTransformationService.transformForDualAxisChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
      )
    }

    // Breakdown + single metric: use multi-line transformation
    if (breakdowns.length >= 1) {
      return dataTransformationService.transformForMultiLineChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
      )
    }

    // No breakdown + single metric: use basic chart transformation
    return dataTransformationService.transformForBasicChart(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    return (
      <LineChartRenderer
        transformedData={props.transformedData}
        data={props.data}
        colorReferenceData={props.colorReferenceData}
        selectedMetrics={props.selectedMetrics}
        selectedBreakdowns={props.selectedBreakdowns}
        onSegmentClick={props.onSegmentClick}
        onDataPointHover={props.onDataPointHover}
        fontSizePx={props.fontSizePx}
        showXAxisLabel={props.showXAxisLabel}
        showYAxisLabel={props.showYAxisLabel}
        showDatapointLabels={props.showDatapointLabels}
        datapointLabelsMode={props.datapointLabelsMode}
        xAxisOverflow={props.xAxisOverflow}
        yAxisDomain={props.yAxisDomain}
        showSentimentZones={props.showSentimentZones}
        showTrendline={props.showTrendline}
        referenceBounds={props.referenceBounds}
        disableAnimation={props.disableAnimation}
      />
    )
  }

  public getTitle(): string {
    return 'Posts Over Time'
  }
}
